import type { Dispatch, MouseEvent, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import UserAvatar from '../../components/UserAvatar';
import { formatUserName } from '../../utils/format/formatUserName';
import ModalFrame from '../primitives/ModalFrame';
import type { LikeHistoryEntry } from './types';


export interface LikesHistoryModalProps {
  showLikesHistory: boolean;
  setShowLikesHistory: Dispatch<SetStateAction<boolean>>;
  likesHistory: LikeHistoryEntry[];
  handleShowUserTooltip: (e: MouseEvent, user: { first_name?: string | null; last_name?: string | null; username?: string | null }) => void;
  formatDate: (dateString: string | null | undefined) => string;
  t: TFunction;
}

export default function LikesHistoryModal({
  showLikesHistory,
  setShowLikesHistory,
  likesHistory,
  handleShowUserTooltip,
  formatDate,
  t,
}: LikesHistoryModalProps) {
  return (
    <ModalFrame
      isOpen={showLikesHistory}
      onClose={() => setShowLikesHistory(false)}
      variant="dialog"
      showBackButton={true}
      title={t("Likes history")}
    >
      {likesHistory && likesHistory.length > 0 ? (
        <div className="flex flex-col gap-xl">
          {likesHistory.map((entry: LikeHistoryEntry, index: number) => (
            <div
              key={index}
              className="flex items-start gap-lg rounded-md border border-white-alpha-10 bg-white-alpha-5 p-lg"
            >
              <UserAvatar
                user={{
                  photo_url: entry.user.photo_url,
                  first_name: entry.user.first_name,
                  last_name: entry.user.last_name,
                  username: entry.user.username,
                  vip_until: entry.user.vip_until,
                }}
                size={48}
                alt={entry.user.first_name || "Unknown"}
                onClick={(e) => handleShowUserTooltip(e, entry.user)}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-xs">
                <div
                  onClick={(e) => handleShowUserTooltip(e, entry.user)}
                  className="cursor-pointer [font-family:Lato,sans-serif] text-[16px] leading-[20px] font-bold text-white"
                >
                  {(() => {
                    const { shortName, usernameFormatted, displayName } = formatUserName(entry.user);
                    return shortName ? (
                      <>{shortName}{usernameFormatted && <span className="font-normal text-text-quieter"> {usernameFormatted}</span>}</>
                    ) : displayName;
                  })()}
                </div>
                <div className="[font-family:Lato,sans-serif] text-[14px] leading-[18px] font-normal text-text-subtle">
                  {formatDate(entry.created_at)}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-3xl text-center [font-family:Lato,sans-serif] text-[16px] leading-[24px] font-normal text-text-subtle">
          {t("No likes history available")}
        </div>
      )}
    </ModalFrame>
  );
}
